import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
    loadDocumentTypeRequirementDescriptionsSuccess,
    loadDocumentTypeRequirementDescriptionsFailure,
    loadDocumentTypeTextRequirementsFailure,
    loadDocumentTypeTextRequirementsSuccess,
    searchDocumentTypesByName,
    searchDocumentTypesByNameFailure,
    searchDocumentTypesByNameSuccess,
    selectedDocumentType
} from 'src/app/compliance/store/actions/document-reviewer.actions';
import {
    selectComplianceCandidateDocument,
    selectComplianceContractSelected
} from 'src/app/compliance/store/selectors/compliance.selectors';
import { DocumentTypesClientWrapper } from '../../document-types-client-wrapper';
import { DocumentTypeRequirementTextDto } from '../../api/models/document-type-requirement-text-dto.model';
import { RequirementDescriptionClient } from '../../api/requirement-description-client.service';
import { DocumentTypeConfigurationRequirementDto } from '../../api/models/document-type-configuration-requirement-dto.model';
import { ApiException } from '../../api/misc/api-exception.error';

@Injectable()
export class DocumentTypesEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _documentTypesService: DocumentTypesClientWrapper,
        private readonly _store: Store,
        private readonly _requirementDescriptionClient: RequirementDescriptionClient
    ) {}

    searchDocumentTypesByName$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(searchDocumentTypesByName),
            concatLatestFrom(() => [
                this._store.select(selectComplianceContractSelected),
                this._store.select(selectComplianceCandidateDocument)
            ]),
            switchMap(([action, contract, document]) => {
                if (contract && document) {
                    return this._documentTypesService
                        .searchDocumentTypes(
                            contract.lineOfBusinessId ?? document.lineOfBusinessId,
                            action.searchText,
                            contract.facilityId
                        )
                        .pipe(
                            map((types) => searchDocumentTypesByNameSuccess({ types: types })),
                            catchError((result) => of(searchDocumentTypesByNameFailure({ error: result })))
                        );
                } else {
                    return of(searchDocumentTypesByNameSuccess({ types: [] }));
                }
            })
        );
    });

    getDocumentTypeTextRequirements$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(selectedDocumentType),
            concatLatestFrom(() => this._store.select(selectComplianceContractSelected)),
            switchMap(([action, contract]) => {
                if (action.documentType?.hasRequirements && contract?.facilityId) {
                    return this._documentTypesService
                        .getDocumentTypeTextRequirements(action.documentType.id, contract.facilityId)
                        .pipe(
                            map((result: DocumentTypeRequirementTextDto[]) =>
                                loadDocumentTypeTextRequirementsSuccess({ documentTextRequirements: result })
                            ),
                            catchError((error) => of(loadDocumentTypeTextRequirementsFailure({ error: error })))
                        );
                } else {
                    return of(loadDocumentTypeTextRequirementsSuccess({ documentTextRequirements: [] }));
                }
            })
        );
    });

    getDocumentTypeRequirementsDescriptionByContractId$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(selectedDocumentType),
            concatLatestFrom(() => this._store.select(selectComplianceContractSelected)),
            switchMap(([action, contract]) => {
                if (action.documentType?.hasRequirements && contract?.facilityId) {
                    return this._requirementDescriptionClient
                        .getWinningDescriptionsByContractId(action.documentType.id, contract.id, true, false)
                        .pipe(
                            map((result: DocumentTypeConfigurationRequirementDto[]) =>
                                loadDocumentTypeRequirementDescriptionsSuccess({
                                    documentTypeRequirementDescriptions: result
                                })
                            ),
                            catchError((error) => {
                                if (ApiException.isApiException(error)) {
                                    return of(
                                        loadDocumentTypeRequirementDescriptionsFailure({
                                            error: JSON.parse(JSON.stringify(error))
                                        })
                                    );
                                }
                                return of(loadDocumentTypeRequirementDescriptionsFailure({ error: error }));
                            })
                        );
                } else {
                    return of(
                        loadDocumentTypeRequirementDescriptionsSuccess({ documentTypeRequirementDescriptions: [] })
                    );
                }
            })
        );
    });
}
